import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Sparkles, Plus, X } from 'lucide-react';
import { toast } from 'sonner';

interface Scenario {
  label: string;
  change: number;
}

interface ScenarioResult {
  label: string;
  change_percent: number;
  fx_rate: number;
  normalized_cash: number;
  normalized_burn: number;
  runway_months: number;
}

interface RunwaySensitivityProps {
  companyId: string;
}

export default function CompanyRunwaySensitivityCard({ companyId }: RunwaySensitivityProps) {
  const [base, setBase] = useState('USD');
  const [scenarios, setScenarios] = useState<Scenario[]>([
    { label: 'Strong USD', change: 5 },
    { label: 'Neutral', change: 0 },
    { label: 'Weak USD', change: -5 },
  ]);
  const [results, setResults] = useState<ScenarioResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    if (!scenarios.length) {
      toast.error('Add at least one scenario');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase.functions.invoke('fx-scenario', {
        body: {
          companyId,
          base,
          horizonDays: 90,
          scenarios,
        },
      });

      if (error) throw error;

      if (!data || !data.results) {
        throw new Error('No data returned from sensitivity analysis');
      }

      setResults(data.results);
      toast.success('Runway sensitivity calculated');
    } catch (e: any) {
      console.error('Runway sensitivity error:', e);
      const errorMessage = e.message || 'Failed to calculate sensitivity';
      setError(errorMessage);

      // Show user-friendly error messages
      if (errorMessage.includes('No financial data')) {
        toast.error('Please add cash balance and burn rate in Company Settings first');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  }

  function addScenario() {
    setScenarios([...scenarios, { label: `Scenario ${scenarios.length + 1}`, change: 0 }]);
  }

  function removeScenario(idx: number) {
    setScenarios(scenarios.filter((_, i) => i !== idx));
  }

  function updateScenario(idx: number, field: keyof Scenario, value: string | number) {
    const updated = [...scenarios];
    updated[idx] = { ...updated[idx], [field]: value };
    setScenarios(updated);
  }

  return (
    <Card className="border-border bg-card">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">Runway Sensitivity (FX Shocks)</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              See how currency movements affect your runway
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={base} onValueChange={setBase}>
              <SelectTrigger className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="ZAR">ZAR</SelectItem>
                <SelectItem value="EUR">EUR</SelectItem>
                <SelectItem value="GBP">GBP</SelectItem>
                <SelectItem value="AED">AED</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" onClick={run} disabled={loading}>
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-1" />
                  Run
                </>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Scenario Builder */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Scenarios</Label>
            <Button size="sm" variant="outline" onClick={addScenario} className="h-7 text-xs">
              <Plus className="h-3 w-3 mr-1" />
              Add
            </Button>
          </div>

          <div className="grid gap-2">
            {scenarios.map((scenario, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Input
                  placeholder="Label"
                  value={scenario.label}
                  onChange={(e) => updateScenario(idx, 'label', e.target.value)}
                  className="flex-1 h-9 text-sm"
                />
                <div className="flex items-center gap-1">
                  <span className="text-sm text-muted-foreground">Δ FX %</span>
                  <Input
                    type="number"
                    value={scenario.change}
                    onChange={(e) => updateScenario(idx, 'change', parseFloat(e.target.value))}
                    className="w-20 h-9 text-sm"
                  />
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => removeScenario(idx)}
                  className="h-9 w-9 p-0"
                  disabled={scenarios.length === 1}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="border border-destructive/50 rounded-lg p-4 bg-destructive/5">
            <p className="text-sm text-destructive font-medium mb-2">
              Unable to calculate sensitivity
            </p>
            <p className="text-xs text-muted-foreground">
              {error.includes('No financial data')
                ? "Add your company's cash balance and monthly burn rate in Company Settings → Financial Data to enable runway sensitivity analysis."
                : error}
            </p>
          </div>
        ) : results.length > 0 ? (
          <div className="space-y-2">
            <Label className="text-sm font-medium">Results</Label>
            <div className="grid sm:grid-cols-3 gap-2">
              {results.map((r) => (
                <div key={r.label} className="border border-border rounded-lg p-3 bg-background">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{r.label}</p>
                      <p className="text-xs text-muted-foreground">
                        {r.change_percent > 0 ? '+' : ''}
                        {r.change_percent}% FX
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-semibold">
                        {r.runway_months ? r.runway_months.toFixed(1) : '—'}
                      </p>
                      <p className="text-xs text-muted-foreground">months</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-sm text-muted-foreground mb-2">
              Configure scenarios and click Run to see how currency movements affect your runway
            </p>
            <p className="text-xs text-muted-foreground">
              Requires cash balance and burn rate data
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
